import * as React from 'react';
import * as cx from 'classnames';
import SuperCritical from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';

import {
    RotationDashboard,
    PageMode,
    PilotGroupingViewModel,
    PilotSectionViewModel,
    PilotViewModel,
    EditSectionViewModel,
    DeleteSectionViewModel
} from './model';

import { ActionLoadingIndicator } from 'core/actionloadingindicator/view';
import { PCModal } from 'portcall/pcmodal/view';
import { PCSortable } from 'portcall/pcsortable/view';
import { PersonProfilePopupView } from 'portcall/personprofilepopup/view';
import { PilotsHeaderView, PilotsHeaderTab } from 'pilots/pilotsheader/view';
import { ValidationErrorView } from 'core/validationerror/view';

export const RotationDashboardView = SuperCritical(({ model: m } : { model : RotationDashboard }) =>
    <div className="rotationdashboard pc-page-fixedwidth">
        {/*<loadingindicator params="request: Request" />*/}
        {m.Model.Navbar() &&
            <PilotsHeaderView
                selectedTab={PilotsHeaderTab.Rotation}
                title="Rotation Dashboard"
                model={m.Model.Navbar()!}
            />
        }
        <PageBody model={m} />
        <EditSectionModal model={m} />
        <DeleteSectionModal model={m} />
    </div>
);

let PageBody = SuperCritical(({ model: m } : { model : RotationDashboard }) =>
    <div className="pc-card page-body">
        <PageLeftSide model={m} />
        <PageRightSide model={m} />
    </div>
);

let PageLeftSide = SuperCritical(({ model: m } : { model : RotationDashboard }) =>
    <div className="left-side">
        <PCSortable
            className="sections-in-agency"
            onSort={m.OnSortSections}
            items={m.Sections}
            rowFactory={s =>
                <PilotSectionView section={s} model={m} key={s.Model.Id()} />
            }
            disabled={() => m.PageMode() !== PageMode.ManageSections}
        />
        <div className="add-section">
            {m.ShowAddSection() &&
                <a href="" className="add-section-link" onClick={noDefault(m.AddSection)}>+ Add Section</a>
            }
        </div>
    </div>
);

let PilotSectionView = SuperCritical(({ section: s, model: m } : { section : PilotSectionViewModel, model : RotationDashboard }) =>
    <li className={cx({
            'cursor-auto': m.PageMode() !== PageMode.ManageSections
        })}>
        <div className={cx('section-header', {
                'space-headers': m.PageMode() === PageMode.ManageSections
            })}>
            {m.PageMode() === PageMode.ManageSections ?
                <span>
                    <a href="" className="delete-link"    onClick={noDefault(s.ShowDeleteSection)}>Delete</a>
                    <a href="" className="edit-name-link" onClick={noDefault(s.ShowEditName)}>{s.Model.Name()}</a>
                </span> :
                <span>{s.Model.Name()}</span>
            }
        </div>
        {m.PageMode() !== PageMode.ManageSections &&
            <PilotsList group={s} model={m} className="pilots-in-section" />
        }
    </li>
);

let PageRightSide = SuperCritical(({ model: m } : { model : RotationDashboard }) =>
    <div className="right-side">
        <div className="page-description pc-text-smalllabel">
            This is a list of all the pilots in the system. Pilots will appear in this order throughout the
            system, such as on the Assignments page. Select a row to jump to that pilot's profile.
        </div>
        <ActionsView model={m} />
        <div className="unassigned-pilots-area">
            <div className="unassigned-pilots-header">Unassigned Pilots</div>
            <PilotsList group={m.UnassignedPilots} model={m} className="unassigned-pilots-list" />
        </div>
    </div>
);

let ActionsView = SuperCritical(({ model: m } : { model : RotationDashboard }) =>
    m.Model.CanEdit() ?
        <div className="pc-card actions-box">
            <div className="pc-card-header pc-card-title">Actions</div>
            <PageModeSelector model={m}
                pageMode={PageMode.SetRotationOrder}
                linkText="Set Rotation Order"
                // NOTE: {'...'} gets compiled to a JS string as is, so escapes like \u201C are preserved and respected in the compiled output.
                // "..." gets treated as HTML in compilation, so escapes like \u201C get escaped AGAIN and shown to the user as literal strings.
                // So options are {'...'} with embedded JS escapes, or "..." with embedded HTML escapes. I prefer the JS escapes.
                // See https://reactjs.org/docs/jsx-in-depth.html#string-literals
                description={'Drag pilots into sections. Order the pilots in the desired rotation (the \u201Cbatting order\u201D).'}
            />
            <PageModeSelector model={m}
                pageMode={PageMode.ManageSections}
                linkText="Manage Sections"
                description="Add, remove, edit, and reorder the sections on the left. Drag section headers to reorder."
            />
        </div> : null
);

let PageModeSelector = SuperCritical(({ model: m, pageMode, linkText, description } : {
    model : RotationDashboard,
    pageMode : PageMode,
    linkText : string,
    description : string
}) => {
    const selected = m.PageMode() === pageMode;
    return (
        <div className={cx('action', { 'selected': selected })}>
            {selected &&
                <a href="" className="done-link" onClick={noDefault(() => m.PageMode(PageMode.View))}>Done</a>
            }
            <a href="" className="main-link" onClick={noDefault(() => m.PageMode(pageMode))}>{linkText}</a>
            <div className="description pc-text-smalllabel">{description}</div>
        </div>
    );
});

let PilotsList = SuperCritical(({ group: g, model: m, className } : {
    group : PilotGroupingViewModel,
    model : RotationDashboard,
    className : string
}) =>
    <PCSortable
        className={cx('connected-pilot-lists', className)}
        onSort={g.OnSortPilots}
        items={g.Pilots}
        rowFactory={p =>
            <PilotView pilot={p} model={m} key={p.Model.Id()} />
        }
        disabled={() => m.PageMode() !== PageMode.SetRotationOrder}
        connectWith={() => '.connected-pilot-lists'}
    />
);

let PilotView = SuperCritical(({ pilot: p, model: m } : { pilot : PilotViewModel, model : RotationDashboard }) =>
    <li className={cx({
            'cursor-click': m.PageMode() !== PageMode.SetRotationOrder
        })}>
        <div className="row-contents"
            onClick={m.PageMode() === PageMode.SetRotationOrder ? undefined : p.ToggleProfile}>
            {p.DisplayName()}
        </div>
        <PersonProfilePopupView modelContainer={p.PersonProfilePopup} />
    </li>
);

let EditSectionModal = SuperCritical(({ model: m } : { model : RotationDashboard }) => {
    const editSection = m.EditSectionModal();
    return (
        <PCModal className="editsection" shown={() => !!editSection} onBackDropClick={editSection?.Cancel}>
            <div className="modal-content pc-card">
                <div className="pc-card-header editsection-title">Rename Section</div>
                <ActionLoadingIndicator loading={() => !!editSection && editSection.Loading()} />
                {editSection &&
                    <form onSubmit={noDefault(editSection.SaveChanges)}>
                        <div className="editsection-body">
                            <div className="edit-name">
                                <label>Enter a new section name:</label>
                                <input className="edit-name-input"
                                    value={editSection.Model.Name()}
                                    onChange={e => editSection.Model.Name(e.currentTarget.value)} />
                            </div>
                            <ValidationErrorView error={() => editSection.Validation()['Name']} />
                        </div>
                        <div className="editsection-footer">
                            <a href="" onClick={noDefault(editSection.Cancel)}>Cancel</a>
                            <span className="pc-icon-bullet"></span>
                            <a href="" onClick={noDefault(editSection.SaveChanges)}>Change Name</a>
                        </div>
                    </form>
                }
            </div>
        </PCModal>
    );
});

let DeleteSectionModal = SuperCritical(({ model: m } : { model : RotationDashboard }) => {
    const deleteSection = m.DeleteSectionModal();
    return (
        <PCModal className="deletesection" shown={() => !!deleteSection} onBackDropClick={deleteSection?.Cancel}>
            <div className="modal-content">
                <div className="modal-body clearfix">
                    <h2 className="modal-title">Delete Section</h2>
                    <ActionLoadingIndicator loading={() => !!deleteSection && deleteSection.Loading()} />
                    <div>
                        Are you sure you want to delete this section?
                        Any pilots will be moved into the unassigned section.
                    </div>
                    {deleteSection &&
                        <div className="action-btns">
                            <a className="pc-cancel-link" onClick={noDefault(deleteSection.Cancel)}>Cancel</a>
                            {' '}
                            <button className="pc-button" onClick={noDefault(deleteSection.Delete)}>Delete</button>
                        </div>
                    }
                </div>
            </div>
        </PCModal>
    );
});


// WEBPACK FOOTER //
// ./apps/pilots/rotationdashboard/view.tsx